<script setup lang="ts">
/**
 * 登录 / 注册。
 *
 * 三条容易踩的线：
 * 1. **手机号必须归一化成 E.164**。契约的 `PhoneSchema` 只接受 `+` 开头，
 *    用户输入的 `138 0013 8000` 直接发过去就是 40001。
 * 2. **倒计时用一次性 interval，不是常驻定时器**。常驻的话每次登录页挂载
 *    都多一个永不停的计时器；两条通道的冷却都归零时立刻 stopTimer。
 * 3. **42901 要采纳服务端给的 Retry-After**，而不是固执地用自己的 60 秒。
 *    服务端知道真正的冷却还剩多久。
 *
 * 页面上有**两条验证码通道（手机号 / 邮箱）加一条口令通道（管理员 / ROOT）**，
 * 刻意不做成几个页面、几个路由：它们只是同一件事的不同凭据，成功后都落到同一个
 * 会话状态里。拆开意味着「我该去哪个页面登录」这个本不该存在的问题，
 * 以及几套要同步维护的错误处理。
 *
 * 排布按设计稿定稿：顶部 tab 只放**面向所有人**的两条验证码通道，管理员口令入口
 * 下移到「其他方式」分隔线下面、点击后在原处展开——它不是第三种"登录方式"，
 * 而是少数人走的旁路；占着最显眼的 tab，会让普通用户以为自己缺一个管理员账号。
 * 但入口对所有人可见、不靠"藏起按钮"挡人：权限判定只发生在服务端（普通用户走
 * 口令通道拿 40100），藏着它只会让后台使用者以为自己拿到了错的地址。
 *
 * 「哪个面板该显示」「副标题说什么」这类判断全在 `../lib/login-mode.ts` 里，
 * 因为仓库只跑 `src` 下的 `.test.ts`（node 环境，没有组件测试），
 * 写在模板的三元表达式里就只能靠手点验证。
 */

import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { EmailSchema, ErrorCode, PasswordSchema, UsernameSchema } from '@spirit/contracts';

import Button from '../components/ui/Button.vue';
import Input from '../components/ui/Input.vue';
import { pushToast } from '../components/ui/toast-store';
import { isApiError } from '../lib/api-client';
import {
  clearAuthError,
  isStaffRole,
  passwordLogin,
  sendEmailCode,
  sendSmsCode,
  signOut,
  useAuth,
  verifyEmailCode,
  verifySmsCode,
} from '../lib/auth-store';
import { WECHAT_WEB_LOGIN_ENABLED, isMockEnabled } from '../lib/env';
import { maskPhone } from '../lib/format';
import {
  LOGIN_MODE_TABS,
  SMS_LOGIN_DISABLED_NOTICE,
  SMS_LOGIN_ENABLED,
  activeLoginPanel,
  loginPanelSubtitle,
  sanitizeCodeInput,
  type LoginMode,
} from '../lib/login-mode';
// 归一化放在 lib 里：`<script setup>` 不允许具名导出，而这是个值得单测的纯函数。
import { normalizePhone } from '../lib/phone';

/** 服务端没给 cooldownSeconds 时的兜底。 */
const FALLBACK_COOLDOWN_SECONDS = 60;

const router = useRouter();
const route = useRoute();
const auth = useAuth();

/**
 * 默认落点：手机号通道被停用时直接给邮箱面板。
 * tab 还在原处可点（点开是一句说明），但第一眼不该是一块"已关闭"。
 */
const mode = ref<LoginMode>(SMS_LOGIN_ENABLED ? 'sms' : 'email');
/** 管理员口令面板是否展开：它不是 tab，而是「其他方式」区里点出来的一个面板。 */
const staffExpanded = ref(false);
const phone = ref('');
const code = ref('');
const email = ref('');
const emailCode = ref('');
const username = ref('');
const password = ref('');
const localError = ref<string | null>(null);
const sending = ref(false);
const verifying = ref(false);
const submittingPassword = ref(false);

/**
 * 两条通道各自计时。
 *
 * 共用一个计数器会出现"在手机号 tab 点过发送，切到邮箱 tab 顶着手机号通道的冷却"——
 * 服务端按通道分别限流，界面也必须分别显示。
 */
const cooldown = reactive<Record<LoginMode, number>>({ sms: 0, email: 0 });

// Input.vue 用 defineOptions({ inheritAttrs: false })，但 ref 绑在原生 input 上，
// 所以这里拿到的是 HTMLInputElement 本身，可以直接 focus()。
const codeInputRef = ref<HTMLInputElement | null>(null);
const emailCodeInputRef = ref<HTMLInputElement | null>(null);

let timer: number | null = null;

function stopTimer(): void {
  if (timer !== null) {
    window.clearInterval(timer);
    timer = null;
  }
}

/** 两条通道任意一条还在冷却就保持同一个 interval 在跑；都归零时由 watch 停掉。 */
watch(
  () => cooldown.sms > 0 || cooldown.email > 0,
  (ticking) => {
    if (!ticking) {
      stopTimer();
      return;
    }
    if (timer !== null) return;
    timer = window.setInterval(() => {
      cooldown.sms = cooldown.sms > 0 ? cooldown.sms - 1 : 0;
      cooldown.email = cooldown.email > 0 ? cooldown.email - 1 : 0;
    }, 1000);
  },
);

onBeforeUnmount(stopTimer);

const normalizedPhone = computed(() => normalizePhone(phone.value));
/**
 * 邮箱先过契约的 `EmailSchema`（它自带 trim + 小写 + 254 上限）。
 *
 * 用解析结果而不是原串去请求：`User@Example.com` 与服务端唯一索引里的
 * `user@example.com` 必须是同一个账号，大小写差异不该变成第二个账号。
 */
const emailTarget = computed<string | null>(() => {
  const parsed = EmailSchema.safeParse(email.value);
  return parsed.success ? parsed.data : null;
});
const panel = computed(() => activeLoginPanel(mode.value, staffExpanded.value));
const panelSubtitle = computed(() => loginPanelSubtitle(panel.value));
const busy = computed(() => auth.pending || sending.value || verifying.value || submittingPassword.value);
const shownError = computed(() => localError.value ?? auth.error);
const needsLogin = computed(() => auth.hydrated && auth.status !== 'authenticated');
const isStaff = computed(() => isStaffRole(auth.role));

/** 已登录卡片上的身份：手机号优先，邮箱登录的用户没有手机号，退回邮箱。 */
const identityLabel = computed(() => {
  if (isStaff.value) return auth.user?.nickname ?? '管理员';
  const boundPhone = auth.user?.phone ?? null;
  if (boundPhone !== null && boundPhone.length > 0) return maskPhone(boundPhone);
  const boundEmail = auth.user?.email ?? null;
  return boundEmail !== null && boundEmail.length > 0 ? boundEmail : maskPhone(null);
});

/**
 * 登录成功后的回跳地址（守卫送来的 `?redirect=/admin/users`）。
 *
 * 只接受**站内绝对路径**：以单个 `/` 开头。两个容易漏的点——
 * `//evil.com` 是协议相对地址（浏览器会跳到外站），所以 `startsWith('//')` 必须排掉；
 * `query.redirect` 也可能是数组（`?redirect=a&redirect=b`），取第一个而不是直接当字符串用。
 * 校验不过就返回 null，由调用方决定默认落点：**绝不因为一个畸形的 query 把用户送去站外**。
 */
const postLoginTarget = computed<string | null>(() => {
  const raw = route.query.redirect;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value;
});

/**
 * 切换登录方式时清掉上一条错误。
 *
 * 不清的话会出现"我在手机上试错一次，切到账号密码，还没提交就顶着一行红色报错"——
 * 那行错误说的是另一种凭据的事。`auth.error` 也要一起清：`useAuth()` 只暴露只读
 * getter，视图自己删不掉它，所以 auth-store 专门导出了 `clearAuthError()`。
 */
watch(mode, () => {
  localError.value = null;
  clearAuthError();
});

/**
 * 点 tab 隐含"我要用验证码登录"，所以要顺手收起口令面板。
 *
 * 不收起的话 `activeLoginPanel()` 会继续把 `staff` 排在前面（展开状态优先），
 * 点了 tab 却什么也没变，看起来像按钮坏了。
 */
function selectMode(next: LoginMode): void {
  staffExpanded.value = false;
  mode.value = next;
}

function toggleStaffPanel(): void {
  staffExpanded.value = !staffExpanded.value;
  localError.value = null;
  clearAuthError();
}

/**
 * 发码成功后的统一收尾：冷却秒数、提示、把光标送进验证码框。
 *
 * 两条通道的差别只有"送码到哪"，收尾逐字相同；不共用一个实现的话，以后只会改到一半。
 */
function applySendResult(
  channel: LoginMode,
  result: { expireSeconds: number; cooldownSeconds: number },
  focusCodeInput: HTMLInputElement | null,
): void {
  cooldown[channel] =
    result.cooldownSeconds > 0 ? result.cooldownSeconds : FALLBACK_COOLDOWN_SECONDS;
  pushToast({
    tone: 'success',
    title: '验证码已发送',
    description: `${Math.max(1, Math.round(result.expireSeconds / 60))} 分钟内有效`,
  });
  focusCodeInput?.focus();
}

/** 发码失败的统一收尾：42901 采纳服务端的 Retry-After，其余照抄服务端文案。 */
function applySendError(channel: LoginMode, caught: unknown): void {
  if (!isApiError(caught)) {
    localError.value = '发送失败，请稍后重试';
    return;
  }
  localError.value = caught.message;
  if (caught.code === ErrorCode.RATE_LIMITED && caught.retryAfterSeconds !== undefined) {
    cooldown[channel] = caught.retryAfterSeconds;
  }
}

/** 验证码登录成功后的统一落点：回跳地址优先，否则进对话。 */
async function finishCodeLogin(): Promise<void> {
  pushToast({ tone: 'success', title: '登录成功' });
  await router.replace(postLoginTarget.value ?? '/chat');
}

async function handleSendCode(): Promise<void> {
  const target = normalizedPhone.value;
  if (target === null) {
    localError.value = '请输入 11 位手机号';
    return;
  }
  sending.value = true;
  localError.value = null;
  try {
    applySendResult('sms', await sendSmsCode(target), codeInputRef.value);
  } catch (caught) {
    applySendError('sms', caught);
  } finally {
    sending.value = false;
  }
}

async function handleSendEmailCode(): Promise<void> {
  const target = emailTarget.value;
  if (target === null) {
    localError.value = '请输入有效的邮箱地址';
    return;
  }
  sending.value = true;
  localError.value = null;
  try {
    applySendResult('email', await sendEmailCode(target), emailCodeInputRef.value);
  } catch (caught) {
    applySendError('email', caught);
  } finally {
    sending.value = false;
  }
}

async function handleVerify(): Promise<void> {
  const target = normalizedPhone.value;
  if (target === null) {
    localError.value = '请输入 11 位手机号';
    return;
  }
  if (!/^\d{6}$/.test(code.value)) {
    localError.value = '请输入 6 位数字验证码';
    return;
  }
  verifying.value = true;
  localError.value = null;
  try {
    await verifySmsCode(target, code.value);
    await finishCodeLogin();
  } catch (caught) {
    localError.value = isApiError(caught) ? caught.message : '登录失败，请稍后重试';
  } finally {
    verifying.value = false;
  }
}

async function handleVerifyEmail(): Promise<void> {
  const target = emailTarget.value;
  if (target === null) {
    localError.value = '请输入有效的邮箱地址';
    return;
  }
  if (!/^\d{6}$/.test(emailCode.value)) {
    localError.value = '请输入 6 位数字验证码';
    return;
  }
  verifying.value = true;
  localError.value = null;
  try {
    await verifyEmailCode(target, emailCode.value);
    await finishCodeLogin();
  } catch (caught) {
    localError.value = isApiError(caught) ? caught.message : '登录失败，请稍后重试';
  } finally {
    verifying.value = false;
  }
}

/**
 * 账号密码登录。
 *
 * 前端预校验**复用契约里的两个 schema**，不手写正则：账号规则（哪些字符合法、
 * 是否大小写敏感）只有一处真源，服务端改了规则前端跟着改，不会出现
 * "前端说格式对、服务端说格式错"这种最费口舌的报错。
 *
 * 提交前的 trim / toLowerCase 也交给 `UsernameSchema`——`PasswordLoginSchema` 里
 * 用的就是它，用户在登录页输入 `Admin` 时不该自己去猜大小写。
 */
async function handlePasswordSubmit(): Promise<void> {
  const parsedUsername = UsernameSchema.safeParse(username.value);
  if (!parsedUsername.success) {
    localError.value = parsedUsername.error.issues[0]?.message ?? '请输入账号';
    return;
  }
  const parsedPassword = PasswordSchema.safeParse(password.value);
  if (!parsedPassword.success) {
    localError.value = parsedPassword.error.issues[0]?.message ?? '请输入密码';
    return;
  }

  submittingPassword.value = true;
  localError.value = null;
  try {
    await passwordLogin(parsedUsername.data, parsedPassword.data);
    pushToast({ tone: 'success', title: '登录成功' });
    // 回跳地址优先；没有就按身份决定落点：管理员 / ROOT 进后台，其余照旧进对话。
    await router.replace(
      postLoginTarget.value ?? (isStaffRole(auth.role) ? '/admin/overview' : '/chat'),
    );
  } catch (caught) {
    // 用服务端文案：40100（不是后台账号）与 40001（格式）必须能被用户区分开。
    localError.value = isApiError(caught) ? caught.message : '登录失败，请稍后重试';
  } finally {
    submittingPassword.value = false;
  }
}

/**
 * 分段控件的选中态样式，与 QuotaView 的 tabClass 同一套写法，避免各页各造一种胶囊。
 *
 * 选中态跟的是**实际展开的面板**而不是 `mode`：口令面板展开时两个 tab 都不该高亮，
 * 否则 `aria-selected` 与实际显示的内容会互相矛盾。
 */
function modeTabClass(value: LoginMode): string {
  return panel.value === value
    ? 'flex-1 rounded-md bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-900'
    : 'flex-1 rounded-md px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700';
}

async function handleSignOut(): Promise<void> {
  await signOut();
  await router.push('/');
}
</script>

<template>
  <div class="mx-auto w-full max-w-md px-4 py-12">
    <!-- 已登录：给一个明确出口，而不是继续显示登录表单 -->
    <div v-if="!needsLogin && auth.status === 'authenticated'" class="rounded-xl border border-slate-200 bg-white p-6 text-center">
      <h1 class="text-lg font-semibold text-slate-900">你已登录</h1>
      <p class="mt-2 text-sm text-slate-600">当前账号：{{ identityLabel }}</p>
      <div class="mt-6 flex flex-wrap justify-center gap-3">
        <RouterLink :to="isStaff ? '/admin/overview' : '/chat'">
          <Button>{{ isStaff ? '进入后台' : '去对话' }}</Button>
        </RouterLink>
        <Button variant="secondary" @click="() => void handleSignOut()">退出登录</Button>
      </div>
    </div>

    <template v-else>
      <h1 class="text-xl font-semibold text-slate-900">登录 / 注册</h1>
      <p class="mt-1.5 text-sm leading-relaxed text-slate-600">{{ panelSubtitle }}</p>

      <!-- 两条验证码通道共用一个页面：入口对所有人可见，权限判定只发生在服务端，
           前端不靠"藏起按钮"来挡人——那既挡不住也让人找不到正确的入口。 -->
      <div
        class="mt-5 flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5"
        role="tablist"
        aria-label="登录方式"
      >
        <button
          v-for="tab in LOGIN_MODE_TABS"
          :key="tab.value"
          type="button"
          role="tab"
          :aria-selected="panel === tab.value"
          :class="modeTabClass(tab.value)"
          @click="selectMode(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <form
        v-if="panel === 'sms' && SMS_LOGIN_ENABLED"
        class="mt-6 space-y-4"
        @submit.prevent="() => void handleVerify()"
      >
        <Input
          id="login-phone"
          v-model="phone"
          label="手机号"
          type="tel"
          inputmode="numeric"
          autocomplete="tel"
          placeholder="13800138000"
          hint="中国大陆手机号可省略 +86，提交时会自动补全为 E.164 格式。"
        />

        <div class="flex items-start gap-2">
          <div class="min-w-0 flex-1">
            <Input
              id="login-code"
              ref="codeInputRef"
              :model-value="code"
              label="验证码"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              placeholder="6 位数字"
              :hint="isMockEnabled() ? 'Mock 模式下固定验证码是 123456' : '5 分钟内有效'"
              @update:model-value="(value) => (code = sanitizeCodeInput(String(value)))"
            />
          </div>
          <!-- mt-[1.625rem] 对齐的是输入框而不是标签：label 1.25rem + gap 0.375rem -->
          <Button
            type="button"
            variant="secondary"
            class="mt-[1.625rem] shrink-0"
            :disabled="busy || cooldown.sms > 0 || normalizedPhone === null"
            @click="() => void handleSendCode()"
          >
            {{ cooldown.sms > 0 ? `${cooldown.sms} 秒后重发` : '获取验证码' }}
          </Button>
        </div>

        <p role="alert" aria-live="polite" class="min-h-[1.25rem] text-xs text-red-600">
          {{ shownError ?? '' }}
        </p>

        <Button type="submit" size="lg" class="w-full" :disabled="busy">
          {{ verifying ? '登录中…' : '登录' }}
        </Button>
      </form>

      <!-- test_server 分支临时停用手机号验证码登录：服务端会以 50300 拒绝 /auth/sms/*，
           所以这里不渲染表单，只留一句"改走哪条路"。恢复见 lib/login-mode.ts 的开关。 -->
      <div
        v-else-if="panel === 'sms'"
        class="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-800"
        role="status"
      >
        {{ SMS_LOGIN_DISABLED_NOTICE }}
      </div>

      <form v-else-if="panel === 'email'" class="mt-6 space-y-4" @submit.prevent="() => void handleVerifyEmail()">
        <Input
          id="login-email"
          v-model="email"
          label="邮箱"
          type="email"
          inputmode="email"
          autocomplete="email"
          placeholder="you@example.com"
          hint="验证码发送到这个邮箱。未注册的邮箱会自动创建账号，注册时填写的邮箱会成为账号标识。"
        />

        <div class="flex items-start gap-2">
          <div class="min-w-0 flex-1">
            <Input
              id="login-email-code"
              ref="emailCodeInputRef"
              :model-value="emailCode"
              label="验证码"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              placeholder="6 位数字"
              :hint="isMockEnabled() ? 'Mock 模式下固定验证码是 123456' : '5 分钟内有效'"
              @update:model-value="(value) => (emailCode = sanitizeCodeInput(String(value)))"
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            class="mt-[1.625rem] shrink-0"
            :disabled="busy || cooldown.email > 0 || emailTarget === null"
            @click="() => void handleSendEmailCode()"
          >
            {{ cooldown.email > 0 ? `${cooldown.email} 秒后重发` : '获取验证码' }}
          </Button>
        </div>

        <p role="alert" aria-live="polite" class="min-h-[1.25rem] text-xs text-red-600">
          {{ shownError ?? '' }}
        </p>

        <Button type="submit" size="lg" class="w-full" :disabled="busy">
          {{ verifying ? '登录中…' : '登录 / 注册' }}
        </Button>
      </form>

      <div class="mt-6 flex items-center gap-3 text-xs text-slate-400">
        <span class="h-px flex-1 bg-slate-200" />
        <span>其他方式</span>
        <span class="h-px flex-1 bg-slate-200" />
      </div>

      <div class="mt-3 space-y-3">
        <!-- 管理员入口按设计稿放在「其他方式」下面：点它在原处展开口令表单，
             不跳路由、不弹窗——弹窗会盖住刚填的验证码，跳路由会把回跳地址丢掉。 -->
        <div>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            class="w-full"
            :aria-expanded="panel === 'staff'"
            @click="toggleStaffPanel"
          >
            {{ panel === 'staff' ? '收起管理员登录' : '管理员登录' }}
          </Button>

          <form
            v-if="panel === 'staff'"
            class="mt-3 space-y-4 rounded-xl border border-slate-200 bg-white p-4"
            @submit.prevent="() => void handlePasswordSubmit()"
          >
            <Input
              id="login-username"
              v-model="username"
              label="账号"
              type="text"
              autocomplete="username"
              placeholder="管理员账号"
              hint="3-32 位小写字母、数字、下划线、点或连字符，大小写不敏感。"
            />

            <Input
              id="login-password"
              v-model="password"
              label="密码"
              type="password"
              autocomplete="current-password"
              placeholder="至少 8 位"
              hint="账号密码通道仅管理员与 ROOT 可用。"
            />

            <p role="alert" aria-live="polite" class="min-h-[1.25rem] text-xs text-red-600">
              {{ shownError ?? '' }}
            </p>

            <Button type="submit" size="lg" class="w-full" :disabled="busy">
              {{ submittingPassword ? '登录中…' : '登录' }}
            </Button>
          </form>
        </div>

        <div>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            class="w-full"
            disabled
            :title="
              WECHAT_WEB_LOGIN_ENABLED
                ? '微信网页登录需要后端 OAuth 回调，当前契约尚未冻结该端点'
                : '未配置 VITE_WECHAT_WEB_LOGIN=1，微信登录不可用'
            "
          >
            微信登录（暂不可用）
          </Button>
          <p class="mt-2 text-xs leading-relaxed text-slate-500">
            微信网页登录需要后端提供 OAuth 回调地址并冻结回调参数，当前契约里还没有这个端点，因此按钮先占位。
          </p>
        </div>
      </div>

      <p class="mt-6 text-xs leading-relaxed text-slate-500">
        继续即表示你同意平台仅将咨询内容用于生成建议与改进服务。平台不构成医疗诊断，
        紧急情况请拨打 12356 或 110 / 120。
      </p>
    </template>
  </div>
</template>
